import {
	BaseServiceSetup,
	communicationService,
	eventsRepository,
	isExperimentEnabledSSE,
	UapLoadStatus,
	utils,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
// eslint-disable-next-line no-restricted-imports
import { isUapOrRoadblockV2 } from '../../../../core/utils/condition/roadblock-slots-visibility';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../../../communication/events/events-ad-engine-uap';

const logGroup = 'UcpDesktopStickyRightRailExperimentWithMultipleICB';

/* Separating this out from the base sticky-rail-experiment.
 * This uses the same experiment eligibility logic, but with the addition of the repeated ICB unit. */
@Injectable()
export class UcpDesktopStickyRightRailExperimentWithMultipleICBSetup extends BaseServiceSetup {
	/** The below values are set by the server-side experiment code. @see {@Link SSExperiment.call}.
	 *  The addExperimentGroupToTargeting() is called when AdEngine starts, and sources the variant or control groups
	 *  from the classes that are embedded by UCP on the <html> tag. Sourcing and setting and the experiment groups in
	 *  the experiment setup code is redundant, and creates a duplicates entry for no reason.
	 *
	 *  The experiment is being re-run, and triggered through VCL changes in the following PR - https://github.com/Wikia/wikia-vcl/pull/4132
	 *
	 *  control: 'aden-14744-control'
	 *  variant1: 'aden-14744-variant-1'
	 */

	private readonly CONTENT_SELECTOR = '.mw-parser-output > p';
	// The height of each HTML element inside the right-rail-wrapper is 1050px.
	// We add an extra 50px to account for any padding or margin on any ad unit or module,
	// or any small miscalculations due to resizing of the above.
	private readonly PRE_WRAPPER_HEIGHT = 1100;
	// The max amount of ICB units that should show up in the right rail.
	private readonly MAX_ICB_UNITS = 10;

	call(): void {
		const experimentVariant = isExperimentEnabledSSE(
			this.instantConfig,
			'aden-14744',
			'stickyRightRailWithICB',
			logGroup,
		);

		if (experimentVariant) {
			this.configure(experimentVariant);
		}
	}

	private waitForUapAndRailOnICBWrappers(): void {
		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			const isUap = isUapOrRoadblockV2(action, false);
			utils.logger(logGroup, 'UapLoadStatus resolved. isUap is: ', isUap);

			communicationService.on(eventsRepository.RAIL_READY, () => {
				const maxRepeat = isUap ? 1 : this.MAX_ICB_UNITS;
				utils.logger(
					logGroup,
					'Rail is ready. Adding ICB wrappers. isUap value: ',
					isUap,
					'. Max repeat amount will be: ',
					maxRepeat,
				);
				this.addIncontentBoxAdSliderWrappers(maxRepeat);
			});
		});
	}

	/**
	 * Adds the maximum number of InContent BoxAd (ICB) units that can fit within the right rail.
	 *
	 * This method calculates the available space within the right rail and appends the appropriate
	 * number of ICB wrappers based on the height of each unit. It emits an event once the ICB units
	 * are added.
	 *
	 * @param {number} maxAllowed - The maximum number of ICB units allowed.
	 */
	private addIncontentBoxAdSliderWrappers(maxAllowed: number): void {
		const rightRailWrapper = document.querySelector('.right-rail-wrapper');
		const repeatLimit = this.determineAmountOfIcbUnitsInRightRail(maxAllowed);
		utils.logger(logGroup, 'Repeat limit for ICB units in right rail: ', repeatLimit);

		for (let i = 1; i <= repeatLimit; i++) {
			const wrapper = document.createElement('div');
			wrapper.classList.add(
				'wds-viewability-slider__module',
				'incontent-boxad-wrapper',
				`incontent-boxad-${i}`,
			);
			rightRailWrapper?.append(wrapper);
		}

		communicationService.emit(eventsRepository.STICKY_RAIL_DESKTOP_MULTI_ICB_READY, {
			repeatAmount: repeatLimit,
		});
	}

	/**
	 * Determines if an element is a sticky module and visible.
	 *
	 * @param {HTMLElement} element - The element to check.
	 * @returns {boolean} True if the element is a module and visible, false otherwise.
	 */
	private isElementAStickyModuleAndVisible(element: HTMLElement): boolean {
		// These checks are being specifically done for elements that have a display:none property set
		// due to the work that was done on the left panel, which caused them to be invisible.
		// We only care about calculating slider modules that are visible. They will have a preset height of 1050px.
		return (
			element.classList.contains('wds-viewability-slider__module') &&
			window.getComputedStyle(element).display !== 'none'
		);
	}

	/**
	 * Determines the number of InContent BoxAd (ICB) units that can fit within the right rail.
	 *
	 * This method calculates the available space within the right rail and determines how many
	 * ICB units can be added based on the height of each unit. A max of 10 ICB units can be added.
	 *
	 * @returns {number} The number of ICB units that can fit within the right rail.
	 */
	private determineAmountOfIcbUnitsInRightRail(maxAllowed = 10): number {
		const rightRailWrapper = document.querySelector('.right-rail-wrapper');
		if (!rightRailWrapper) {
			return 0;
		}

		// *** Optimization ***
		// If only one ICB unit is allowed, don't do additional calculations.
		// We currently don't do any calculations for the single ICB unit called incontent_boxad.
		// In this case, incontent_boxad_1 will be returned. If, maxAllowed is 1,
		// then we're also most likely in a takeover/UAP.
		if (maxAllowed === 1) {
			utils.logger('Aborting rail calculations due to 1 ICB unit being returned.');
			return 1;
		}

		// Static right rail modules that will show up on every page as part of UCP and AdEngine code
		const rightRailAdModuleSelectors = ['#rail-boxad-wrapper', '.mid-boxad-wrapper'];
		const rightRailAdModuleElements: HTMLElement[] = rightRailAdModuleSelectors.map((selector) => {
			return document.querySelector(selector) as HTMLElement;
		});

		try {
			let occupiedTotalHeight = 0;
			rightRailWrapper.childNodes.forEach((child) => {
				if (child.nodeType === Node.ELEMENT_NODE) {
					const childAsElement = child as HTMLElement;
					const childHeight = childAsElement.offsetHeight;
					utils.logger(logGroup, 'About to call adModules match on element: ', child);

					// Don't calculate and add the heights of the predetermined ad slot modules.
					// They will be added later with a predetermined height of 1050px.
					if (!rightRailAdModuleElements.includes(childAsElement)) {
						const addedHeight = this.isElementAStickyModuleAndVisible(childAsElement)
							? this.PRE_WRAPPER_HEIGHT
							: childHeight;
						occupiedTotalHeight += addedHeight;
						utils.logger(
							logGroup,
							`Adding height of element to occupiedTotalHeight: ${addedHeight}. Total occupied height is now: ${occupiedTotalHeight}`,
						);
					}
				}
			});

			// Pad the heights of the rightRail ad units that may render and be wrapped at a later time.
			occupiedTotalHeight += rightRailAdModuleSelectors?.length * this.PRE_WRAPPER_HEIGHT;

			utils.logger(
				logGroup,
				'Calculated total height of current elements in right rail',
				occupiedTotalHeight,
				' | RightRail total height: ',
				rightRailWrapper.clientHeight,
			);

			const freeHeightSpaceInRightRail = rightRailWrapper.clientHeight - occupiedTotalHeight;
			utils.logger(
				logGroup,
				'Calculated free height space in right rail',
				freeHeightSpaceInRightRail,
			);

			const roundedDownIcbUnitCount = Math.floor(
				freeHeightSpaceInRightRail / this.PRE_WRAPPER_HEIGHT,
			);
			utils.logger(
				logGroup,
				'Calculated amount of ICB units that can fit in right rail',
				roundedDownIcbUnitCount,
				'Max allowed is: ',
				maxAllowed,
			);
			// Return only the max allowed ICB units if the calculated amount is higher
			return Math.min(maxAllowed, roundedDownIcbUnitCount);
		} catch (e) {
			// In case something goes wrong, abort by returning no ICB units
			utils.logger(logGroup, 'Error determining amount of ICB units in right rail', e);
			return 0;
		}
	}

	private configureExperimentVariant(): void {
		// Add a marker class to the body to adjust the right rail styling
		document.body.classList.add('sticky-right-rail-enabled');
		utils.addExperimentToContext('stickyRightRailWithICB', true);

		utils.logger(logGroup, 'setting up waitForUap');

		// TWEAK: support moving DiscordIntegratorModule whenever it loads
		const moveDiscordIntegratorModule = () => {
			utils.logger(logGroup, 'DiscordIntegratorModule hook, moving module');
			const el = document.querySelector('.DiscordIntegratorModule');
			if (!el) {
				utils.logger(logGroup, 'DiscordIntegratorModule not found, skipping');
				return;
			}
			const rail = document.getElementById('WikiaRail');
			if (!rail || !rail.parentElement) {
				utils.logger(logGroup, 'WikiaRail not found, skipping');
				return;
			}
			el.classList.add('wds-viewability-slider__sticky');
			// add wrapper around DiscordIntegratorModule to allow it to be sticky
			const wrapper = document.createElement('div');
			wrapper.classList.add('wds-viewability-slider__module');
			wrapper.classList.add('discord-wrapper');
			wrapper.prepend(el as Node);
			rail.parentElement.insertBefore(wrapper, rail);
		};

		if (document.querySelector('.DiscordIntegratorModule')) {
			moveDiscordIntegratorModule();
		} else {
			// @ts-ignore Use global MW API, it's all fine, TS checks are disabled
			if (window.mw && window.mw.hook) {
				utils.logger(logGroup, 'DiscordIntegratorModule hook found, using mw.hook');
				window.mw.hook('DiscordIntegrator.added').add(moveDiscordIntegratorModule);
			} else {
				// window.mw not found, fallback to using MutationObserver on #WikiaRail to detect when .DiscordIntegratorModule is added
				const rail = document.getElementById('WikiaRail');
				if (rail) {
					utils.logger(logGroup, 'DiscordIntegratorModule hook not found, using MutationObserver');
					const observer = new MutationObserver(() => {
						if (document.querySelector('.DiscordIntegratorModule')) {
							moveDiscordIntegratorModule();
							observer.disconnect();
						}
					});
					observer.observe(rail, { childList: true });
				} else {
					utils.logger(
						logGroup,
						'WikiaRail not found, DiscordIntegratorModule hook not found, skipping moving it',
					);
				}
			}
		}

		// observe the rail, if #mod_boxad module is added and it doesn't have .wds-viewability-slider__sticky,
		// wrap it in wrapper, add the class
		if (document.querySelector('.WikiaRail')) {
			const WikiaRail = document.querySelector('.WikiaRail') as HTMLElement;

			const wrapMidBoxad = () => {
				const midBoxad = document.getElementById('mid_boxad');
				if (!midBoxad || midBoxad.classList.contains('wds-viewability-slider__sticky')) {
					// mid_boxad not found or already wrapped, skipping
					return;
				}
				const afterElement = document.querySelector('.render-new-wiki-recommendations');
				if (!afterElement || !afterElement.parentElement) {
					utils.logger(logGroup, '.render-new-wiki-recommendations not found, skipping');
					return;
				}
				utils.logger(logGroup, 'mid_boxad found, wrapping it');

				midBoxad.classList.add('wds-viewability-slider__sticky');
				// add wrapper around mid_boxad to allow it to be sticky
				const wrapper = document.createElement('div');
				wrapper.classList.add('wds-viewability-slider__module');
				wrapper.classList.add('mid-boxad-wrapper');
				wrapper.prepend(midBoxad);
				afterElement.after(wrapper);
			};
			if (document.getElementById('mid_boxad')) {
				wrapMidBoxad();
			}
			const observer = new MutationObserver(wrapMidBoxad);
			observer.observe(WikiaRail, { childList: true });
		} else {
			utils.logger(logGroup, '.WikiaRail not found, skipping mid_boxad wrapping');
		}

		// TWEAK: add observer on body element and if there's .uap-sticked we need to add the css height of .top-ads-container as a --additional-top-margin value to .wds-viewablity-slider
		// #top_leaderboard slot classes:
		// 1) sticky has: gpt-ad ad-slot expanded-slot bfaa-template slot-responsive theme-hivi uap-toc-pusher
		// 2) normal has: gpt-ad ad-slot a9-ad expanded-slot sticky-tlb
		const slider = document.querySelector('.wds-viewability-slider') as HTMLElement;
		if (slider) {
			const adjustTopMargin = () => {
				let additionalTopMargin = 0;

				// check if there's `.uap-toc-pusher` on #top_leaderboard
				// AND if there's `.uap-sticked` on body
				const topLeaderboard = document.getElementById('top_leaderboard');
				if (
					topLeaderboard &&
					topLeaderboard.classList.contains('uap-toc-pusher') &&
					document.body.classList.contains('uap-sticked')
				) {
					additionalTopMargin = topLeaderboard.clientHeight;
				}
				slider.style.setProperty('--additional-top-margin', `${additionalTopMargin}px`);
			};
			const observer2 = new MutationObserver(adjustTopMargin);
			observer2.observe(document.body, { attributes: true, attributeFilter: ['class'] });
			observer2.observe(document.querySelector('.top-ads-container') as Node, {
				attributes: true,
				attributeFilter: ['class'],
				childList: true,
			});
			adjustTopMargin();
		}
		/*
		The ICB wrappers need to be added in towards the end, to give the other modules a chance to load in.
		The best case scenario here for modules such as Discord is to wait for them to be wrapped in the sticky module
		at JS execution, not when the MW hooks are called, such as for the Discord module.
		Waiting for the MW hooks to be called could cause significant delay in the ICB unit creation, causing view-ability issues.

		Other ad units that will always render be placed on the page, and depend on AdEngine placement will always have a precalculated
		module height of 1050px, since they will be lazy loaded in, and cannot have a proper height calculation determined at the time
		when the experiment setup code is running. Those units include the top_boxad and mid_boxad that always render in the right_rail.
		*/
		this.waitForUapAndRailOnICBWrappers();
	}

	private cleanupExperimentCode(): void {
		utils.addExperimentToContext('stickyRightRailWithICB', false);

		// we need to cleanup the sclasses from html because the layour breaks on smaller screen

		// remove all instances of .wds-viewablity-slider__module and .wds-viewablity-slider__sticky
		const removeClasses = () => {
			document.querySelectorAll('.wds-viewability-slider__module').forEach((el) => {
				el.classList.remove('wds-viewability-slider__module');
			});
			document.querySelectorAll('.wds-viewability-slider__sticky').forEach((el) => {
				el.classList.remove('wds-viewability-slider__sticky');
			});
		};
		removeClasses();
		// add observer on .WikiaRail to remove classes on mutation (for lazy loading modules)
		const observer = new MutationObserver(() => {
			removeClasses();
		});
		const rail = document.querySelector('.WikiaRail');
		if (rail) {
			utils.logger(logGroup, 'Adding cleanup observer to WikiaRail for lazy loading modules');
			observer.observe(rail, { childList: true });
		}
	}

	public configure(experimentVariant: string): void {
		if (experimentVariant === 'variant1') {
			utils.logger(logGroup, 'Variant 1 triggered');
			if (this.isLongPage()) {
				this.configureExperimentVariant();
				return;
			}
			// shorter pages will need to be cleaned up (below)
		} else if (experimentVariant === 'control') {
			utils.logger(logGroup, 'Control triggered');
		}
		// cleanup some when page's too short on variant or the control, or outside of the experiment
		this.cleanupExperimentCode();
	}

	private isLongPage(): boolean {
		const paragraphs = Array.from(document.querySelectorAll(this.CONTENT_SELECTOR));
		let characterCount = 0;

		for (const paragraph of paragraphs) {
			characterCount += paragraph.textContent?.length ?? 0;

			if (characterCount >= 4000) {
				utils.logger(logGroup, '4k+ characters found. Page is a long page.');
				return true;
			}
		}

		utils.logger(logGroup, 'Less than 4k characters found. Page is a short page.');
		return false;
	}
}
